import { z } from 'zod';
import {
  PointSchema,
  MultiPointSchema,
  LineStringSchema,
  MultiLineStringSchema,
  PolygonSchema,
  MultiPolygonSchema,
} from './geojson';

/**
 * ArcGIS public-feature render contract (#204) — served by GET /api/arcgis-features.
 * Backed directly by the `arcgis_public_features` ingest table (migration 0063,
 * `tsr-arcgis-feeds` worker, public GOC ArcGIS FeatureServers). NO fusion — a raw
 * ingested operational signal.
 *
 * The marquee consumer is the COP **Evacuation Orders & Alerts** layer
 * (`feed_key = 'geobc-evac-orders'`, `category = 'evacuation'`): GeoBC / Emergency
 * Management BC order-and-alert AREA polygons carrying affected population / homes.
 * The endpoint is feed-agnostic (the other ingested feeds — drought / tsunami /
 * high-water — share the same shape) and defaults to the evacuation category.
 *
 * Geometry is the affected AREA (Polygon or MultiPolygon), SERVER-SIDE SIMPLIFIED
 * (ST_SimplifyPreserveTopology) before transit — the upstream order/alert payloads
 * run to multiple MB, so the raw geometry would choke the map.
 *
 * Two shapes: the LIST endpoint returns the feature + simplified geometry; the
 * DETAIL endpoint (`/:id`) adds the provenance tail (issuing agency, source URL,
 * ingested_at) for the panel footer.
 */

/** Evacuation escalation register — the `severity` value for evac features. */
export const EVAC_SEVERITIES = ['order', 'alert'] as const;
export type EvacSeverity = (typeof EVAC_SEVERITIES)[number];

/**
 * The feature geometry. The marquee evacuation/drought-basin/SoLE features are
 * AREA polygons, but the generalized arcgis renderer also carries POINT feeds (MB
 * drought river/lake stations, MB boil-water systems, NHC cyclone forecast
 * positions) and LINE feeds (a cyclone track) — so the contract accepts any of the
 * GeoJSON primitives the ingested feeds emit (#213).
 */
export const ArcgisGeometrySchema = z.union([
  PointSchema,
  MultiPointSchema,
  LineStringSchema,
  MultiLineStringSchema,
  PolygonSchema,
  MultiPolygonSchema,
]);
export type ArcgisGeometry = z.infer<typeof ArcgisGeometrySchema>;

export const ArcgisFeaturePropertiesSchema = z.object({
  id: z.string().uuid(),
  /** Feed discriminator (e.g. 'geobc-evac-orders'). */
  feed_key: z.string(),
  /** Normalized hazard family ('evacuation' | 'drought' | 'flood' | 'tsunami' | …). */
  category: z.string(),
  /** Upstream subtype, when published (BC evac: Landslide / Fire / Flood). */
  event_type: z.string().nullable(),
  /** Human label (evac ORDER_ALERT_NAME). */
  name: z.string().nullable(),
  /** Lifecycle: 'active' | 'cleared' | 'unknown'. */
  status: z.string(),
  /** Normalized escalation/level — for evac, 'order' | 'alert'. The palette register. */
  severity: z.string().nullable(),
  /** Affected population, where the feed provides it. */
  population: z.number().nullable(),
  /** Affected dwellings, where provided. */
  homes: z.number().nullable(),
  /** OGL-Canada / source-agency attribution string. */
  attribution: z.string(),
  /** Upstream snapshot last-updated time (UTC ISO). */
  observed_at: z.string(),
  /** Representative interior point (lon/lat) — convenience anchor for labels. */
  lon: z.number(),
  lat: z.number(),
  /** R2/#478 incident-as-aggregator (migration 0119, founder decision #2). The
   *  EFFECTIVE incident link for the GeoBC evac feed (feed_key='geobc-evac-orders'):
   *  the persisted MANUAL link OR the read-time AUTO-link projection. Non-null ⇒ this
   *  evac feature is a protective-action attribute of that incident. Optional so
   *  non-evac categories / detail-built features stay valid. */
  incident_id: z.string().nullable().optional(),
  /** Provenance of the effective link: 'manual' | 'auto' | null (see cap-alert.ts). */
  incident_link_mode: z.enum(['manual', 'auto']).nullable().optional(),
});
export type ArcgisFeatureProperties = z.infer<typeof ArcgisFeaturePropertiesSchema>;

export const ArcgisFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: ArcgisGeometrySchema,
  properties: ArcgisFeaturePropertiesSchema,
});
export type ArcgisFeature = z.infer<typeof ArcgisFeatureSchema>;

export const ArcgisFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(ArcgisFeatureSchema),
});
export type ArcgisFeatureCollection = z.infer<typeof ArcgisFeatureCollectionSchema>;

/** The detail endpoint adds the provenance tail for the panel footer. */
export const ArcgisFeatureDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: ArcgisGeometrySchema,
  properties: ArcgisFeaturePropertiesSchema.extend({
    /** Issuing agency / regional district, when published (attrs.ISSUING_AGENCY). */
    issuing_agency: z.string().nullable(),
    /** Upstream event start time (UTC ISO), when published. */
    event_start_at: z.string().nullable(),
    /** Source FeatureServer URL (attrs.service_url) — provenance click-through. */
    source_url: z.string().nullable(),
    /** When WE wrote/last-refreshed the row (UTC ISO) — ingest provenance. */
    ingested_at: z.string(),
  }),
});
export type ArcgisFeatureDetail = z.infer<typeof ArcgisFeatureDetailSchema>;
